import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  addCartItem,
  cartEmpty,
  deleteCartItem,
  fetchCarts,
  updateCartItem,
} from "../api/cart";
import { useUserContext } from "./userContext";

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [requestFormItems, setRequestFormItems] = useState(() => {
    const savedItems = localStorage.getItem("requestFormItems");
    return savedItems ? JSON.parse(savedItems) : [];
  });
  const { isAuth } = useUserContext();
  useEffect(() => {
    const initializeCart = async () => {
      try {
        const response = await fetchCarts();
        if (response && response.data && Array.isArray(response.data.items)) {
          setCartItems(response.data.items);
        }
      } catch (error) {
        console.error("Error fetching cart on page load:", error.message);
      }
    };

    if (isAuth) {
      initializeCart();
    }
  }, [isAuth]);

  const addToCart = async (item, quantity) => {
    try {
      const response = await addCartItem([
        { filter_id: item.id, type: "product", quantity, ref: item.jl_number },
      ]);
      if (!response || !response.data || !Array.isArray(response.data.items)) {
        setCartItems(cartItems);
        return;
      }
      setCartItems(response.data.items);
    } catch (error) {
      console.error("Error adding to cart:", error.message);
      setCartItems(cartItems);
    }
  };
  const addMultipleToCart = async (items) => {
    try {
      const response = await addCartItem(items);
      if (!response || !response.data || !Array.isArray(response.data.items)) {
        setCartItems(cartItems);
        return;
      }
      setCartItems(response.data.items);
    } catch (error) {
      console.error("Error adding to cart:", error.message);
      setCartItems(cartItems);
    }
  };
  const memoizedRequestFormItems = useMemo(
    () => requestFormItems,
    [requestFormItems]
  );

  useEffect(() => {
    localStorage.setItem(
      "requestFormItems",
      JSON.stringify(memoizedRequestFormItems)
    );
  }, [memoizedRequestFormItems]);

  const addToRequestForm = useCallback(
    (item, quantity) => {
      const existingItem = memoizedRequestFormItems.find(
        (reqItem) => reqItem.id === item.id
      );

      if (item.type === "machine") {
        quantity = 1;
      }

      if (existingItem) {
        setRequestFormItems((prevItems) =>
          prevItems.map((reqItem) =>
            reqItem.id === item.id
              ? {
                  ...reqItem,
                  quantity:
                    item.type === "machine" ? 1 : reqItem.quantity + quantity,
                }
              : reqItem
          )
        );
      } else {
        setRequestFormItems((prevItems) => [
          ...prevItems,
          { ...item, quantity: quantity },
        ]);
      }
    },
    [memoizedRequestFormItems]
  );

  const updateItemQuantity = async (id, quantity) => {
    try {
      const updatedItems = cartItems.map((cartItem) =>
        cartItem.filter_id === id ? { ...cartItem, quantity } : cartItem
      );
      setCartItems(updatedItems);

      const response = await updateCartItem(quantity, id);
      if (response && response.data && Array.isArray(response.data.items)) {
        setCartItems(response.data.items);
      }
    } catch (error) {
      console.error("Error updating cart item:", error.message);
    }
  };

  const removeItemFromCart = async (id) => {
    try {
      const updatedCarts = cartItems.filter((r) => r.filter_id !== id);
      setCartItems(updatedCarts);
      await deleteCartItem(id);
    } catch (error) {
      console.error("Error removing cart item:", error.message);
      setCartItems(cartItems);
    }
  };

  const removeItemFromRequestForm = (id) => {
    const updatedItems = requestFormItems.filter((item) => item.id !== id);
    setRequestFormItems(updatedItems);
  };

  const emptyCart = async () => {
    try {
      await cartEmpty();
      setCartItems([]);
    } catch (error) {
      console.error("Error clearing cart:", error.message);
    }
  };
  const getCarts = async () => {
    try {
      await fetchCarts();
      setCartItems(cartItems);
    } catch (error) {
      console.error("Error fetching cart:", error.message);
    }
  };
  const emptyRequestForm = () => setRequestFormItems([]);

  return (
    <CartContext.Provider
      value={{
        getCarts,
        cartItems,
        requestFormItems,
        addToCart,
        addToRequestForm,
        updateItemQuantity,
        removeItemFromCart,
        removeItemFromRequestForm,
        emptyCart,
        emptyRequestForm,
        setCartItems,
        addMultipleToCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
